import { PageNotice } from '../../../shared/components/PageFeedback';
import { formSchema as userFormSchema } from '../config/workspaceUserForm.schema';
import useRegisteredModuleFilters from '../../moduleFilters';
import IconActions from '../../../shared/components/IconActions';
import React, { useEffect, useRef, useState } from 'react';
import { copyTextToClipboard } from '../../../shared/utils/clipboard';
import { useSession } from '../../../app/providers/session-provider';
import {
  AdaptiveFilterBar,
  ConfirmActionDialog,
  DataTable,
  PageFrame,
  PageHeader,
  PrimaryActionButton,
  StatusPill,
} from '../../../shared/components/patterns';
import { useWorkspaceUsersData } from '../hooks/useWorkspaceUsersData';
import {
  COMPANY_ROLE_OPTIONS,
  PERMISSION_ROLE_OPTIONS,
  getInviteStatusLabel,
  getInviteStatusTone,
  getPermissionRoleLabel,
  getTeamTypeLabel,
  getUserStatusLabel,
  getUserStatusTone,
  workspaceUsersFilterConfig,
} from '../workspaceUsers.config';
import '../workspace-users.css';

const UPDATEABLE_ROLE_OPTIONS = ['MANAGER', 'STAFF'];

function normalizeRole(role) {
  const normalized = (role || '').trim().replace(/[- ]+/g, '_').toUpperCase();
  if (normalized === 'TENANT_ADMIN') {
    return 'OWNER';
  }
  if (normalized === 'ADMIN' || normalized === 'ACCOUNT_MANAGER') {
    return 'MANAGER';
  }
  return normalized;
}

function canEditRole(user, canManageWorkspaceUserRoles) {
  return canManageWorkspaceUserRoles && UPDATEABLE_ROLE_OPTIONS.includes(user.permissionRole || user.role);
}

function canToggleStatus(user, canManageWorkspaceUsers) {
  return canManageWorkspaceUsers && normalizeRole(user.permissionRole || user.role) !== 'OWNER';
}

function canResetPassword(user, currentUser, canManageWorkspaceUsers) {
  if (!canManageWorkspaceUsers || normalizeRole(user.permissionRole || user.role) === 'OWNER') {
    return false;
  }

  const currentRole = normalizeRole(currentUser?.role);
  if (currentRole === 'SUPER_ADMIN') {
    return currentUser?.workspaceAccess === true;
  }

  return currentRole === 'OWNER';
}

function canTransferOwnership(user, currentUser, canManageWorkspaceUserRoles) {
  if (!canManageWorkspaceUserRoles) {
    return false;
  }

  if (!user.active || normalizeRole(user.permissionRole || user.role) !== 'MANAGER') {
    return false;
  }

  if (currentUser?.id != null && String(currentUser.id) === String(user.id)) {
    return false;
  }

  return true;
}

function canEditIdentity(user, currentUser, canManageWorkspaceUsers) {
  if (!canManageWorkspaceUsers || normalizeRole(user.permissionRole || user.role) === 'OWNER') {
    return false;
  }

  const currentRole = normalizeRole(currentUser?.role);
  if (currentRole === 'SUPER_ADMIN') {
    return currentUser?.workspaceAccess === true;
  }

  return currentRole === 'OWNER';
}

function canDeleteUser(user, currentUser, canManageWorkspaceUsers) {
  if (!canManageWorkspaceUsers || normalizeRole(user.permissionRole || user.role) === 'OWNER') {
    return false;
  }
  if (currentUser?.id != null && String(currentUser.id) === String(user.id)) {
    return false;
  }
  const currentRole = normalizeRole(currentUser?.role);
  return currentRole === 'OWNER' || (currentRole === 'SUPER_ADMIN' && currentUser?.workspaceAccess === true);
}

function canResendInvite(user, canManageWorkspaceUsers) {
  if (!canManageWorkspaceUsers || normalizeRole(user.permissionRole || user.role) === 'OWNER') {
    return false;
  }
  return Boolean(user.inviteStatus) || user.active === false;
}

function buildColumns(data, capabilities) {
  return [
    {
      id: 'name',
      header: 'Nome',
      render: (user) => user.fullName || 'Utente senza nome',
    },
    {
      id: 'email',
      header: 'Email',
      render: (user) => user.email || 'N/D',
    },
    {
      id: 'companyRole',
      header: 'Ruolo',
      render: (user) => user.companyRole || getPermissionRoleLabel(user.permissionRole || user.role),
    },
    {
      id: 'team',
      header: 'Team',
      render: (user) => getTeamTypeLabel(user.teamType),
    },
    {
      id: 'invite',
      header: 'Invito',
      render: (user) => (
        <StatusPill label={getInviteStatusLabel(user)} tone={getInviteStatusTone(user)} />
      ),
    },
    {
      id: 'status',
      header: 'Stato',
      render: (user) => (
        <StatusPill label={getUserStatusLabel(user)} tone={getUserStatusTone(user)} />
      ),
    },
    {
      id: 'permissionRole',
      header: 'Permessi',
      render: (user) => canEditRole(user, capabilities?.canManageWorkspaceUserRoles) ? (
        <select
          className="workspace-users-select workspace-users-role-select"
          value={user.permissionRole || user.role}
          disabled={data.pendingRoleUserId === String(user.id)}
          onChange={(event) => data.changeUserRole(user, event.target.value)}
          aria-label={`Permessi ${user.email}`}
        >
          {PERMISSION_ROLE_OPTIONS.map((role) => (
            <option key={role.value} value={role.value}>
              {role.label}
            </option>
          ))}
        </select>
      ) : getPermissionRoleLabel(user.permissionRole || user.role),
    },
  ];
}

function buildRowActions(data, capabilities, currentUser) {
  return (user) => {
    const actions = [];

    if (canEditIdentity(user, currentUser, capabilities?.canManageWorkspaceUsers)) {
      actions.push({
        id: 'edit',
        iconKey: 'actions.edit',
        label: `Modifica ${user.email}`,
        disabled: data.pendingEditUserId === String(user.id),
        onClick: () => data.openUserEdit(user),
      });
    }

    if (canResetPassword(user, currentUser, capabilities?.canManageWorkspaceUsers)) {
      actions.push({
        id: 'password',
        iconKey: 'actions.key',
        label: `Reimposta password ${user.email}`,
        disabled: data.pendingPasswordUserId === String(user.id),
        onClick: () => data.openPasswordReset(user),
      });
    }

    if (canResendInvite(user, capabilities?.canManageWorkspaceUsers)) {
      actions.push({
        id: 'resend',
        iconKey: 'actions.resend',
        label: `Reinvia invito ${user.email}`,
        disabled: data.pendingInviteUserId === String(user.id),
        onClick: () => data.resendUserInvite(user),
      });
    }

    if (canTransferOwnership(user, currentUser, capabilities?.canManageWorkspaceUserRoles)) {
      actions.push({
        id: 'ownership',
        iconKey: 'actions.ownership',
        label: `Trasferisci ownership a ${user.email}`,
        disabled: data.pendingOwnershipUserId === String(user.id),
        onClick: () => data.openOwnershipTransfer(user),
      });
    }

    if (canToggleStatus(user, capabilities?.canManageWorkspaceUsers)) {
      actions.push({
        id: 'status',
        iconKey: 'actions.power',
        label: user.active ? `Disattiva ${user.email}` : `Attiva ${user.email}`,
        disabled: data.pendingUserId === String(user.id),
        onClick: () => data.toggleUserStatus(user),
      });
    }

    if (canDeleteUser(user, currentUser, capabilities?.canManageWorkspaceUsers)) {
      actions.push({
        id: 'delete',
        iconKey: 'actions.delete',
        label: `Elimina ${user.email}`,
        disabled: data.pendingDeleteUserId === String(user.id),
        onClick: () => data.requestRemoveUser(user),
        tone: 'danger',
      });
    }

    return actions;
  };
}

function Alert({ children, tone = 'success' }) {
  if (!children) {
    return null;
  }
  const Component = tone === 'error' ? PageNotice : 'div';
  return (
    <Component className={`workspace-users-alert workspace-users-alert--${tone} ${tone === 'error' ? 'wt-inline-error' : ''}`} role={tone === 'error' ? 'alert' : 'status'}>
      <span>{children}</span>
    </Component>
  );
}

export default function WorkspaceUsersPage() {
  const { capabilities, currentUser, refreshCurrentUser } = useSession();
  const data = useWorkspaceUsersData();
  const inviteInputRef = useRef(null);
  const [copyFeedback, setCopyFeedback] = useState(null);
  useEffect(() => setCopyFeedback(null), [data.createdInviteLink]);

  async function copyInviteLink() {
    const copied = await copyTextToClipboard(data.createdInviteLink, inviteInputRef.current);
    setCopyFeedback({ copied, message: copied
      ? 'Link invito copiato.'
      : 'Copia automatica non disponibile. Il link è selezionato: premi ⌘C o Ctrl+C per copiarlo.' });
  }
  const [removeRequest, setRemoveRequest] = React.useState(null);
  const [ownershipConfirmOpen, setOwnershipConfirmOpen] = React.useState(false);
  const ownershipCandidateId = data.ownershipCandidate?.id;
  const moduleFilters = useRegisteredModuleFilters('users', data.users);
  const rows = moduleFilters.filterRows(data.filteredUsers || data.users);

  const columns = buildColumns(data, capabilities);
  const rowActions = buildRowActions(
    {
      ...data,
      requestRemoveUser: setRemoveRequest,
    },
    capabilities,
    currentUser
  );

  async function confirmRemoveUser() {
    const user = removeRequest;
    if (!user) return;
    await data.removeUser(user);
    setRemoveRequest(null);
  }

  async function confirmOwnershipTransfer() {
    const transferred = await data.transferOwnership();
    if (transferred) {
      setOwnershipConfirmOpen(false);
      await refreshCurrentUser?.();
    }
  }

  React.useEffect(() => {
    if (ownershipCandidateId) {
      setOwnershipConfirmOpen(true);
    }
  }, [ownershipCandidateId]);

  return (
    <PageFrame filterModel={moduleFilters} className="workspace-users-page">
      <PageHeader
        title="User Workspace"
        description="Gestione centralizzata degli utenti"
      />

      <Alert tone="error">{data.usersError}</Alert>
      <Alert>{data.passwordResetSuccess}</Alert>
      <Alert>{data.ownershipSuccess}</Alert>
      <Alert>{data.editSuccess}</Alert>
      <Alert tone="error">{data.linkedStaffError}</Alert>

      <div className="workspace-users-filter-row">
        <AdaptiveFilterBar
          config={workspaceUsersFilterConfig}
          values={data.filters}
          onChange={data.updateFilters}
          onReset={data.resetFilters}
        />
      </div>

      {capabilities?.canManageWorkspaceUsers ? (
        <section className="workspace-users-form-card">
          <h2 className="workspace-users-form-card__title">Aggiungi un nuovo Utente</h2>
          <form
            className="workspace-users-form-grid"
            onSubmit={(event) => {
              event.preventDefault();
              data.createUser();
            }}
          >
            <label className="workspace-users-field">
              <span>{userFormSchema.field('fullName').label}</span>
              <input
                className="workspace-users-input"
                type="text"
                value={data.createForm.fullName}
                disabled={data.isCreatingUser}
                onChange={(event) => data.updateCreateFormField('fullName', event.target.value)}
              />
            </label>

            <label className="workspace-users-field">
              <span>{userFormSchema.field('email').label}</span>
              <input
                className="workspace-users-input"
                type="email"
                value={data.createForm.email}
                disabled={data.isCreatingUser}
                onChange={(event) => data.updateCreateFormField('email', event.target.value)}
              />
            </label>

            <label className="workspace-users-field">
              <span>Permessi</span>
              <select
                className="workspace-users-select"
                value={data.createForm.permissionRole}
                disabled={data.isCreatingUser}
                onChange={(event) => data.updateCreateFormField('permissionRole', event.target.value)}
              >
                {PERMISSION_ROLE_OPTIONS.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="workspace-users-field">
              <span>Tipo</span>
              <select
                className="workspace-users-select"
                value={data.createForm.teamType}
                disabled={data.isCreatingUser}
                onChange={(event) => data.updateCreateFormField('teamType', event.target.value)}
              >
                <option value="INTERNAL">Interno</option>
              </select>
            </label>

            <label className="workspace-users-field">
              <span>Ruolo</span>
              <input
                className="workspace-users-input"
                type="text"
                list="workspace-company-roles"
                value={data.createForm.companyRole}
                disabled={data.isCreatingUser}
                onChange={(event) => data.updateCreateFormField('companyRole', event.target.value)}
              />
              <datalist id="workspace-company-roles">
                {COMPANY_ROLE_OPTIONS.map((role) => (
                  <option key={role} value={role} />
                ))}
              </datalist>
            </label>

            <label className="workspace-users-field">
              <span>Password Iniziale</span>
              <input
                className="workspace-users-input"
                type="password"
                value={data.createForm.password}
                disabled={data.isCreatingUser}
                onChange={(event) => data.updateCreateFormField('password', event.target.value)}
              />
            </label>

            <label className="workspace-users-field">
              <span>Conferma Password</span>
              <input
                className="workspace-users-input"
                type="password"
                value={data.createForm.confirmPassword}
                disabled={data.isCreatingUser}
                onChange={(event) => data.updateCreateFormField('confirmPassword', event.target.value)}
              />
            </label>

            <div className="workspace-users-create-slot"><IconActions>
              <PrimaryActionButton
                type="submit"
                iconKey="actions.add"
                disabled={data.isCreatingUser}
              >
                {data.isCreatingUser ? 'Creazione...' : 'Crea'}
              </PrimaryActionButton>
            </IconActions></div>
          </form>

          <Alert tone="error">{data.createError}</Alert>
          <Alert>{data.createSuccess}</Alert>
          {data.createdInviteLink ? (
            <div className="workspace-users-invite-link">
              <input
                className="workspace-users-input"
                value={data.createdInviteLink}
                ref={inviteInputRef}
                readOnly
                aria-label="Link invito"
              />
              <button
                type="button"
                className="workspace-users-secondary-button"
                onClick={copyInviteLink}
              >
                Copia link
              </button>
            </div>
          ) : null}
          {copyFeedback ? <p role={copyFeedback.copied ? 'status' : 'alert'} className={copyFeedback.copied ? '' : 'wt-inline-error'}>{copyFeedback.message}</p> : null}
        </section>
      ) : null}

      {data.passwordResetForm.userId ? (
        <section className="workspace-users-inline-card">
          <h2 className="workspace-users-inline-card__title">Reimposta password</h2>
          <p className="workspace-users-inline-card__description">
            Imposta manualmente una nuova password per l'account selezionato.
          </p>
          <form
            className="workspace-users-form-grid"
            onSubmit={(event) => {
              event.preventDefault();
              data.resetUserPassword();
            }}
          >
            <label className="workspace-users-field">
              <span>Nuova password</span>
              <input
                className="workspace-users-input"
                type="password"
                value={data.passwordResetForm.password}
                disabled={data.pendingPasswordUserId === data.passwordResetForm.userId}
                onChange={(event) => data.updatePasswordResetField('password', event.target.value)}
              />
            </label>
            <label className="workspace-users-field">
              <span>Conferma password</span>
              <input
                className="workspace-users-input"
                type="password"
                value={data.passwordResetForm.confirmPassword}
                disabled={data.pendingPasswordUserId === data.passwordResetForm.userId}
                onChange={(event) => data.updatePasswordResetField('confirmPassword', event.target.value)}
              />
            </label>
            <div className="workspace-users-inline-actions"><IconActions>
              <button type="submit" className="workspace-users-primary-button">
                Salva password
              </button>
              <button type="button" className="workspace-users-secondary-button" onClick={data.closePasswordReset}>
                Annulla
              </button>
            </IconActions></div>
          </form>
          <Alert tone="error">{data.passwordResetError}</Alert>
        </section>
      ) : null}

      {data.editForm.userId ? (
        <section className="workspace-users-inline-card">
          <h2 className="workspace-users-inline-card__title">Modifica utente</h2>
          <p className="workspace-users-inline-card__description">
            Aggiorna identita, tipo team e ruolo aziendale dell'utente workspace.
          </p>
          <form
            className="workspace-users-form-grid"
            onSubmit={(event) => {
              event.preventDefault();
              data.updateUserIdentity();
            }}
          >
            <label className="workspace-users-field">
              <span>{userFormSchema.field('fullName').label}</span>
              <input
                className="workspace-users-input"
                type="text"
                value={data.editForm.fullName}
                readOnly={data.users.find((user) => String(user.id) === String(data.editForm.userId))?.inviteStatus !== 'PENDING'}
                disabled={data.pendingEditUserId === data.editForm.userId}
                onChange={(event) => data.updateEditFormField('fullName', event.target.value)}
              />
            </label>
            <label className="workspace-users-field">
              <span>{userFormSchema.field('email').label}</span>
              <input
                className="workspace-users-input"
                type="email"
                value={data.editForm.email}
                readOnly={data.users.find((user) => String(user.id) === String(data.editForm.userId))?.inviteStatus !== 'PENDING'}
                disabled={data.pendingEditUserId === data.editForm.userId}
                onChange={(event) => data.updateEditFormField('email', event.target.value)}
              />
            </label>
            <label className="workspace-users-field">
              <span>Tipo</span>
              <select
                className="workspace-users-select"
                value={data.editForm.teamType}
                disabled={data.pendingEditUserId === data.editForm.userId}
                onChange={(event) => data.updateEditFormField('teamType', event.target.value)}
              >
                <option value="INTERNAL">Interno</option>
              </select>
            </label>
            <label className="workspace-users-field">
              <span>Ruolo</span>
              <input
                className="workspace-users-input"
                type="text"
                list="workspace-company-roles"
                value={data.editForm.companyRole}
                disabled={data.pendingEditUserId === data.editForm.userId}
                onChange={(event) => data.updateEditFormField('companyRole', event.target.value)}
              />
            </label>
            <div className="workspace-users-inline-actions"><IconActions>
              <button type="submit" className="workspace-users-primary-button">
                Salva
              </button>
              <button type="button" className="workspace-users-secondary-button" onClick={data.closeUserEdit}>
                Annulla
              </button>
            </IconActions></div>
          </form>
          <Alert tone="error">{data.editError}</Alert>
        </section>
      ) : null}

      {data.ownershipCandidate ? (
        <section className="workspace-users-inline-card">
          <h2 className="workspace-users-inline-card__title">Trasferisci ownership</h2>
          <p className="workspace-users-inline-card__description">
            {data.ownershipCandidate.fullName || data.ownershipCandidate.email} diventera Owner del tenant.
          </p>
          <div className="workspace-users-inline-actions"><IconActions>
            <button
              type="button"
              className="workspace-users-primary-button"
              disabled={data.pendingOwnershipUserId === String(data.ownershipCandidate.id)}
              onClick={() => setOwnershipConfirmOpen(true)}
            >
              Conferma
            </button>
            <button type="button" className="workspace-users-secondary-button" onClick={data.closeOwnershipTransfer}>
              Annulla
            </button>
          </IconActions></div>
          <Alert tone="error">{data.ownershipError}</Alert>
        </section>
      ) : null}


      <DataTable
        columns={columns}
        rows={rows}
        getRowId={(user) => user.id}
        loading={data.isLoadingUsers}
        emptyState="Nessun utente workspace trovato."
        rowActions={rowActions}
      />
      <ConfirmActionDialog
        open={Boolean(removeRequest)}
        title="Eliminare utente?"
        description="Questa azione rimuoverà l’utente dal workspace. Conferma solo se sei sicuro."
        confirmLabel="Elimina utente"
        cancelLabel="Annulla"
        tone="danger"
        loading={removeRequest ? data.pendingDeleteUserId === String(removeRequest.id) : false}
        onCancel={() => setRemoveRequest(null)}
        onConfirm={confirmRemoveUser}
      />
      <ConfirmActionDialog
        open={ownershipConfirmOpen && Boolean(data.ownershipCandidate)}
        title="Trasferire ownership?"
        description="Stai per trasferire la proprietà del workspace a un altro utente. Dopo la conferma, i permessi cambieranno secondo la logica prevista dal sistema."
        confirmLabel="Trasferisci ownership"
        cancelLabel="Annulla"
        tone="danger"
        loading={data.ownershipCandidate ? data.pendingOwnershipUserId === String(data.ownershipCandidate.id) : false}
        onCancel={() => {
          setOwnershipConfirmOpen(false);
          data.closeOwnershipTransfer?.();
        }}
        onConfirm={confirmOwnershipTransfer}
      />
    </PageFrame>
  );
}
